import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { ApiContractError, ApiError } from '@/api/client'
import type { SalesQuery } from '@/api/sales/types'
import { buildNameFilter, buildOrderBy } from '@/api/sales/api'
import { BarList } from '@/components/charts/BarList'
import { ChartCard } from '@/components/charts/ChartCard'
import {
  isSalesLevel,
  isSalesSortField,
  salesLevelIds,
  salesLevels,
  salesSortFields,
  type SalesLevelId,
} from '@/features/sales/salesLevels'
import {
  collectionSaleRow,
  motiveSaleRow,
  topicGroupSaleRow,
  topicSaleRow,
  topSalesRows,
  type SalesRow,
} from '@/features/sales/salesRows'
import {
  useCollectionSales,
  useMotiveSales,
  useTopicGroupSales,
  useTopicSales,
} from '@/features/sales/useSales'
import { formatNumber } from '@/lib/format'

const PAGE_SIZE = 20

function positiveInteger(value: string | null, fallback: number): number {
  if (value === null) return fallback
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

export function SalesAnalysisPage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()
  const levelValue = searchParams.get('level') ?? ''
  const level: SalesLevelId = isSalesLevel(levelValue) ? levelValue : 'collection'
  const search = (searchParams.get('search') ?? '').trim()
  const sortValue = searchParams.get('sortBy') ?? ''
  const sortBy = isSalesSortField(sortValue) ? sortValue : 'salesTotal'
  const sortDirection = searchParams.get('sortDirection') === 'asc' ? 'asc' : 'desc'
  const page = positiveInteger(searchParams.get('page'), 1)

  const updateParams = (update: (params: URLSearchParams) => void, replace = false): void => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        update(next)
        return next
      },
      { replace },
    )
  }

  const activeLevel = salesLevels[level]
  const nameFilter = buildNameFilter(activeLevel.nameFilterField, search)
  const query: SalesQuery = {
    page,
    pageSize: PAGE_SIZE,
    ...(nameFilter !== undefined ? { filter: nameFilter } : {}),
    orderBy: buildOrderBy(sortBy, sortDirection),
  }

  const collectionSales = useCollectionSales(query, level === 'collection')
  const motiveSales = useMotiveSales(query, level === 'motive')
  const topicGroupSales = useTopicGroupSales(query, level === 'topicGroup')
  const topicSales = useTopicSales(query, level === 'topic')

  const active =
    level === 'collection'
      ? { ...collectionSales, rows: (collectionSales.data?.data ?? []).map(collectionSaleRow) }
      : level === 'motive'
        ? { ...motiveSales, rows: (motiveSales.data?.data ?? []).map(motiveSaleRow) }
        : level === 'topicGroup'
          ? {
              ...topicGroupSales,
              rows: (topicGroupSales.data?.data ?? []).map(topicGroupSaleRow),
            }
          : { ...topicSales, rows: (topicSales.data?.data ?? []).map(topicSaleRow) }

  const rows: readonly SalesRow[] = active.rows
  const totalCount = active.data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">Verkaufsanalyse</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Was läuft gut, was läuft nicht?
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Verkaufszahlen je Kollektion, Motiv, Slogan-Themengruppe oder Slogan-Thema, über sechs
          feste Zeiträume. Die Zahlen zählen Verkäufe, keinen Umsatz und keinen Gewinn: Beides
          liefern diese vier Endpunkte nicht.
        </p>
      </header>

      <div role="tablist" aria-label="Auswertungsebene" className="mb-4 flex flex-wrap gap-2">
        {salesLevelIds.map((id) => {
          const definition = salesLevels[id]
          const isSelected = level === id
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => {
                updateParams((params) => {
                  params.set('level', id)
                  params.delete('page')
                })
              }}
              className={
                isSelected
                  ? 'rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition'
                  : 'rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'
              }
            >
              {definition.label}
            </button>
          )
        })}
      </div>
      <p className="mb-6 text-sm text-slate-600 dark:text-slate-300">{activeLevel.description}</p>

      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Suche</span>
          <input
            type="search"
            value={searchParams.get('search') ?? ''}
            onChange={(event) => {
              updateParams((params) => {
                const value = event.target.value
                if (value.trim() === '') params.delete('search')
                else params.set('search', value)
                params.delete('page')
              }, true)
            }}
            placeholder="Name"
            className="w-64 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Zeitraum</span>
          <select
            value={sortBy}
            onChange={(event) => {
              updateParams((params) => {
                const value = event.target.value
                if (isSalesSortField(value)) params.set('sortBy', value)
                params.delete('page')
              })
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          >
            {salesSortFields.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => {
            updateParams((params) => {
              params.set('sortDirection', sortDirection === 'desc' ? 'asc' : 'desc')
              params.delete('page')
            })
          }}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          {sortDirection === 'desc' ? 'Absteigend' : 'Aufsteigend'}
        </button>
      </div>

      {active.isLoading && (
        <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
          Verkaufszahlen werden geladen…
        </p>
      )}

      {active.error && (
        <div className="rounded border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            {active.error instanceof ApiError || active.error instanceof ApiContractError
              ? active.error.message
              : 'Die Verkaufszahlen konnten nicht geladen werden.'}
          </p>
        </div>
      )}

      {active.data && !active.error && (
        <>
          <ChartCard
            title={`Top ${activeLevel.label} nach ${salesSortFields.find((o) => o.value === sortBy)?.label ?? 'Gesamt'}`}
            description="Aktuelle Seite, bereits serverseitig sortiert."
            className="mb-6"
          >
            <BarList data={topSalesRows(rows)} />
          </ChartCard>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  <th className="px-4 py-2 font-medium">{activeLevel.label}</th>
                  <th className="px-4 py-2 text-right font-medium">7 Tage</th>
                  <th className="px-4 py-2 text-right font-medium">30 Tage</th>
                  <th className="px-4 py-2 text-right font-medium">90 Tage</th>
                  <th className="px-4 py-2 text-right font-medium">365 Tage</th>
                  <th className="px-4 py-2 text-right font-medium">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400"
                    >
                      Keine Zeilen für diese Auswahl.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.key} className="border-b border-slate-100 dark:border-slate-700">
                      <td className="px-4 py-2 text-slate-900 dark:text-slate-100">{row.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatNumber(row.counters.sales7)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatNumber(row.counters.sales30)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatNumber(row.counters.sales90)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatNumber(row.counters.sales365)}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold tabular-nums">
                        {formatNumber(row.counters.salesTotal)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Seite {formatNumber(page)} von {formatNumber(totalPages)} ·{' '}
                {formatNumber(totalCount)} Treffer
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => {
                    updateParams((params) => {
                      params.set('page', String(page - 1))
                    })
                  }}
                  className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Zurück
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => {
                    updateParams((params) => {
                      params.set('page', String(page + 1))
                    })
                  }}
                  className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
